import { isDefined, isNil } from "@clipboard-health/util-ts";
import type SendbirdChat from "@sendbird/chat";
import { ConnectionHandler, ConnectionState, SendbirdErrorCode } from "@sendbird/chat";
import { useEffect, useState } from "react";

type SendbirdConnectionSdk = Pick<SendbirdChat, "connectionState" | "currentUser"> &
  Partial<Pick<SendbirdChat, "addConnectionHandler" | "removeConnectionHandler">>;

const EXPECTED_READ_STATE_ERROR_CODES = new Set<number>([
  SendbirdErrorCode.CONNECTION_REQUIRED,
  SendbirdErrorCode.CONNECTION_CANCELED,
  SendbirdErrorCode.NETWORK_ERROR,
  SendbirdErrorCode.WEBSOCKET_CONNECTION_CLOSED,
  SendbirdErrorCode.WEBSOCKET_CONNECTION_FAILED,
  SendbirdErrorCode.REQUEST_CANCELED,
  SendbirdErrorCode.USER_AUTH_DEACTIVATED,
  SendbirdErrorCode.USER_AUTH_DELETED_OR_NOT_FOUND,
  SendbirdErrorCode.USER_DEACTIVATED,
]);

const I18N_SKIP_CONNECTION_REQUIRED_MESSAGES = new Set([
  "Connection is required",
  "Connection is required.",
]);

export function isSendbirdSdkConnected(sdk: SendbirdConnectionSdk | null | undefined): boolean {
  if (isNil(sdk)) {
    return false;
  }

  return sdk.connectionState === ConnectionState.OPEN && isDefined(sdk.currentUser);
}

export function useIsSendbirdSdkConnected(sdk: SendbirdConnectionSdk | null | undefined): boolean {
  const [handlerId] = useState(
    () => `chat-read-state-connection-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
  const [isConnected, setIsConnected] = useState(() => isSendbirdSdkConnected(sdk));

  useEffect(() => {
    setIsConnected(isSendbirdSdkConnected(sdk));

    if (isNil(sdk) || isNil(sdk.addConnectionHandler) || isNil(sdk.removeConnectionHandler)) {
      return;
    }

    const updateConnectionState = () => setIsConnected(isSendbirdSdkConnected(sdk));

    const connectionHandler = new ConnectionHandler({
      onConnected: updateConnectionState,
      onReconnectSucceeded: updateConnectionState,
      onConnectionLost: () => setIsConnected(false),
      onDisconnected: () => setIsConnected(false),
      onReconnectFailed: () => setIsConnected(false),
      onReconnectStarted: () => setIsConnected(false),
    });

    // Call on `sdk` so the methods keep their `this` binding. Destructuring them
    // detaches `this`, and Sendbird's handlers read `this._iid`, which throws.
    sdk.addConnectionHandler(handlerId, connectionHandler);

    return () => {
      sdk.removeConnectionHandler?.(handlerId);
    };
  }, [handlerId, sdk]);

  return isConnected;
}

export function isExpectedSendbirdReadStateError(error: unknown): boolean {
  if (
    error instanceof Error &&
    error.name === "SendbirdError" &&
    I18N_SKIP_CONNECTION_REQUIRED_MESSAGES.has(error.message)
  ) {
    return true;
  }

  if (typeof error !== "object" || isNil(error) || !("code" in error)) {
    return false;
  }

  const { code } = error;

  return typeof code === "number" && EXPECTED_READ_STATE_ERROR_CODES.has(code);
}
